import classNames from 'classnames'
import { match } from 'ts-pattern'
import { pluralize } from '../../utils/text-utils.ts'
import { toolColorToken, toolDisplayName, toolIndexOfFinding } from './exploit-chains-palette.ts'
import {
  countVerdicts,
  droppedForBudget,
  type Hypothesis,
  type HypothesisVerdict,
  lensLabel,
  type RunFinding,
  runFindingLabel,
  type RunState,
} from './exploit-chains-run.ts'
import * as styles from './hypothesis-log.css.ts'

/**
 * The run as a pentester's notebook. System lines say what was combined and how the proposals
 * were narrowed; each hypothesis gets its own entry that fills in with a verdict and, for a
 * rejection, the verifier's reason — the sentence that makes an empty result believable.
 */

const VERDICT_LABELS: Record<HypothesisVerdict, string> = {
  pending: 'proposed',
  verifying: 'verifying',
  confirmed: 'held',
  rejected: 'did not hold',
  errored: 'errored',
}

const describeTools = (run: RunState): string =>
  (run.tools ?? []).map(tool => `${tool.count} ${toolDisplayName(tool.tool)}`).join(', ')

export type HypothesisLogProps = {
  run: RunState
  /** Whether the run is still going: shows a typing cursor on the hypothesis being verified. */
  isLive?: boolean
  highlightedId?: string
  onHighlightHypothesis?: (hypothesisId: string | undefined) => void
  className?: string
}

export const HypothesisLog: React.FC<HypothesisLogProps> = ({
  run,
  isLive = false,
  highlightedId,
  onHighlightHypothesis,
  className,
}) => {
  const tools = run.tools ?? []
  const dropped = droppedForBudget(run)
  const verdicts = countVerdicts(run)
  const finishedLenses = run.lenses.filter(lens => lens.state !== 'running')

  return (
    <ol className={classNames(styles.log, className)} aria-label="Hypothesis log">
      {run.tools && (
        <SystemEntry>
          Combined{' '}
          <b>
            {run.totalFindings ?? 0} {pluralize(run.totalFindings ?? 0, 'finding', 'findings')}
          </b>
          : {describeTools(run)}
          {run.threatModelGeneratedAt ? ', with the repository’s threat model.' : '.'}
        </SystemEntry>
      )}
      {run.indexed !== undefined && (
        <SystemEntry>
          Indexed <b>{run.indexed}</b> {pluralize(run.indexed, 'finding', 'findings')} for the proposal passes.
          {dropped > 0
            ? ` ${dropped} did not fit the model’s context and ${pluralize(dropped, 'was', 'were')} not considered.`
            : ''}
        </SystemEntry>
      )}
      {run.lenses.map(lens => (
        <SystemEntry key={lens.lens}>
          {match(lens.state)
            .with('running', () => `${lensLabel(lens.lens)} reading the index…`)
            .with('done', () => (
              <>
                {lensLabel(lens.lens)} proposed <b>{lens.proposed}</b>{' '}
                {pluralize(lens.proposed, 'candidate', 'candidates')}.
              </>
            ))
            .with('failed', () => `${lensLabel(lens.lens)} failed; its proposals are missing from this run.`)
            .exhaustive()}
        </SystemEntry>
      ))}
      {run.pool && (
        <SystemEntry>
          {run.pool.raw} {pluralize(run.pool.raw, 'proposal', 'proposals')}
          {finishedLenses.length > 1 ? ` across ${finishedLenses.length} lenses` : ''} → {run.pool.unique} unique →
          judged down to <b>{run.pool.merged}</b> {pluralize(run.pool.merged, 'hypothesis', 'hypotheses')} worth
          verifying against the source.
        </SystemEntry>
      )}
      {run.hypotheses.map((hypothesis, index) => (
        <HypothesisEntry
          key={hypothesis.id}
          index={index + 1}
          hypothesis={hypothesis}
          tools={tools}
          isLive={isLive}
          isHighlighted={highlightedId === hypothesis.id}
          onHighlight={onHighlightHypothesis}
        />
      ))}
      {run.grounding && (
        <SystemEntry>
          Grounded {run.grounding.chainsOut} {pluralize(run.grounding.chainsOut, 'chain', 'chains')}
          {run.grounding.referencesRepaired + run.grounding.referencesDropped > 0
            ? `; ${run.grounding.referencesRepaired} finding ${pluralize(run.grounding.referencesRepaired, 'reference', 'references')} repaired and ${run.grounding.referencesDropped} dropped.`
            : '; every finding reference resolved to a dispatched finding.'}
        </SystemEntry>
      )}
      {run.status === 'completed' && run.hypotheses.length > 0 && (
        <SystemEntry>
          <b>Done.</b> {verdicts.confirmed} of {run.hypotheses.length}{' '}
          {pluralize(run.hypotheses.length, 'hypothesis', 'hypotheses')} held.
        </SystemEntry>
      )}
      {run.status === 'completed' && run.hypotheses.length === 0 && run.pool && (
        <SystemEntry>
          <b>Done.</b> No hypothesis was worth verifying.
        </SystemEntry>
      )}
    </ol>
  )
}

const SystemEntry: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li className={styles.systemEntry}>
    <p className={styles.systemText}>{children}</p>
  </li>
)

type HypothesisEntryProps = {
  index: number
  hypothesis: Hypothesis
  tools: RunState['tools']
  isLive: boolean
  isHighlighted: boolean
  onHighlight?: (hypothesisId: string | undefined) => void
}

const HypothesisEntry: React.FC<HypothesisEntryProps> = ({
  index,
  hypothesis,
  tools = [],
  isLive,
  isHighlighted,
  onHighlight,
}) => (
  <li
    className={classNames(styles.hypothesisEntry, { [styles.hypothesisEntryHighlighted]: isHighlighted })}
    onMouseEnter={() => onHighlight?.(hypothesis.id)}
    onMouseLeave={() => onHighlight?.(undefined)}
    aria-label={`Hypothesis ${index}: ${VERDICT_LABELS[hypothesis.verdict]}`}
  >
    <div className={styles.hypothesisHeader}>
      <span className={styles.lenses}>
        H{index} · {hypothesis.lenses.map(lensLabel).join(' + ') || 'proposed'}
      </span>
      <span className={styles.verdict({ verdict: hypothesis.verdict })}>{VERDICT_LABELS[hypothesis.verdict]}</span>
    </div>
    <p className={styles.hypothesisText}>{hypothesis.hypothesis}</p>
    {hypothesis.findings.length > 0 && (
      <ul className={styles.findingChips} aria-label="Findings in this hypothesis">
        {hypothesis.findings.map(finding => (
          <FindingChip
            key={finding.findingId}
            finding={finding}
            toolIndex={toolIndexOfFinding(tools, finding.findingId)}
          />
        ))}
      </ul>
    )}
    {match(hypothesis.verdict)
      .with('verifying', () =>
        isLive ? (
          <p className={styles.reason}>
            Reading the source
            <span className={styles.cursor} aria-hidden="true" />
          </p>
        ) : null
      )
      .with('confirmed', () => (
        <p className={styles.reason}>
          {hypothesis.title && (
            <b>
              {hypothesis.title}
              {hypothesis.severity ? ` · ${hypothesis.severity}` : ''}
              <br />
            </b>
          )}
          {hypothesis.reason}
        </p>
      ))
      .with('rejected', 'errored', () =>
        hypothesis.reason ? <p className={styles.reason}>{hypothesis.reason}</p> : null
      )
      .with('pending', () => null)
      .exhaustive()}
  </li>
)

const FindingChip: React.FC<{ finding: RunFinding; toolIndex: number }> = ({ finding, toolIndex }) => {
  const tool = finding.tool ? toolDisplayName(finding.tool) : undefined
  return (
    <li
      className={styles.findingChip}
      aria-label={[runFindingLabel(finding), tool, finding.path].filter(Boolean).join(' · ')}
    >
      <span
        className={styles.findingSwatch}
        style={{ background: toolIndex >= 0 ? toolColorToken(toolIndex) : undefined }}
        aria-hidden="true"
      />
      <span className={styles.findingChipLabel}>{runFindingLabel(finding)}</span>
    </li>
  )
}
